import { zip } from 'lodash-es'
import * as React from 'react'
import { Avatar } from 'src/design-system'
import { CollapsibleTable } from 'src/design-system/components/organisms/CollapsibleTable'
import { Status } from 'src/design-system/components/organisms/CollapsibleTable/Status'
import { getSkillRows } from '../current-org-summary/CurrentSkills'
import { Team, User } from '../types'
import { avatarFallback, twoDecimals } from '../utils'
import { useWfiContext } from '../WfiContext'
import styles from './AnalysisCard.module.scss'

const HEADINGS = {
  skill: 'Skill',
  before: 'Before',
  after: 'After',
  change: 'Change',
  required: 'Required',
  status: 'Status',
  peopleUsing: 'People Using',
}

type ProposalSkillsProps = {
  id: number
  selectedTeam: number | null
  teams: Team[]
}

export const ProposalSkills: React.VFC<ProposalSkillsProps> = ({
  id,
  selectedTeam,
  teams,
}) => {
  const { orgSnapshot, analyses, skillRequirements } = useWfiContext()
  const analysis = analyses.find((a) => a.id === id)

  if (analysis == null || orgSnapshot == null) return null

  teams =
    selectedTeam == null
      ? teams
      : teams.filter((team) => team.id === selectedTeam).slice(0, 1)
  const users =
    selectedTeam == null
      ? analysis.users
      : analysis.users.filter((user) => user.team_id === selectedTeam)

  const currentTeams =
    selectedTeam == null
      ? orgSnapshot.teams
      : orgSnapshot.teams.filter((team) => team.id === selectedTeam).slice(0, 1)
  const currentUsers =
    selectedTeam == null
      ? orgSnapshot.users
      : orgSnapshot.users.filter((user) => user.team_id === selectedTeam)

  const currentRows = getSkillRows(
    currentTeams,
    currentUsers,
    orgSnapshot.skills
  )
  const analysisRows = getSkillRows(teams, users, analysis.skills)

  const usersBySkillId = new Map<number, Set<User>>()
  users.forEach((user) => {
    const position = orgSnapshot.positionsById.get(user.position_id)
    position?.requirements.forEach((req) => {
      const users = usersBySkillId.get(req.skill_id) ?? new Set()
      users.add(user)
      usersBySkillId.set(req.skill_id, users)
    })
  })

  const groupedRows = [
    {
      name: 'Skills',
      display: 'Skills',
      rows: zip(currentRows, analysisRows).map(
        ([currentSkill, analysisSkill]) => ({
          skill: currentSkill?.name,
          before: currentSkill?.avgLevel ?? 0,
          after: analysisSkill?.avgLevel ?? 0,
          change: (() => {
            const change =
              Number(analysisSkill?.avgLevel ?? 0) -
              Number(currentSkill?.avgLevel ?? 0)
            const sign = change < 0 ? '' : '+'
            return sign + twoDecimals(change)
          })(),
          required: twoDecimals(
            skillRequirements.get(
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              currentSkill?.id
            ) ?? 3
          ),
          status: (
            <Status
              status={
                Number(analysisSkill?.avgLevel ?? 0) >=
                (skillRequirements.get(
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  currentSkill?.id
                ) ?? 3)
                  ? 'meeting'
                  : 'notMeeting'
              }
            />
          ),
          peopleUsing: (() => {
            const users: User[] = Array.from(
              analysisSkill == null
                ? new Set()
                : usersBySkillId.get(analysisSkill.id)?.values() ?? new Set()
            )
            const avatars = users.slice(0, 3).map((user, i) => (
              <Avatar
                alt={user.name}
                // the Avatar component has a div wrapping it around the div
                // where any custom classNames are passed in,
                // so we can't use `:not(:first-child)` in the CSS
                className={i === 0 ? '' : styles.skillsAvatar}
                fallbackText={avatarFallback(user.name)}
                key={user.id}
                size="xs"
                src={user.avatar_url}
              />
            ))
            return (
              <div className={styles.avatarContainer}>
                <p>{users.length ?? 0}</p>
                {avatars}
              </div>
            )
          })(),
        })
      ),
    },
  ]

  return (
    <div>
      <CollapsibleTable headings={HEADINGS} groupedRows={groupedRows} />
    </div>
  )
}
